import * as React from "react"
import { ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { useUser } from "@/context/UserContext"
import { useBookingT } from "@/hooks/use-translations"
import { IntegrationLogo } from "@/features/integrations/components/IntegrationLogo"
import { StatusBadge } from "@/features/integrations/components/StatusBadge"
import { deriveStatus } from "@/features/integrations/components/statusModel"
import type {
  Connection,
  IntegrationCatalogItem,
  Locale,
} from "@/features/integrations/api/types"

type Props = {
  item: IntegrationCatalogItem
  connection?: Connection | null
  onClick: (id: string) => void
}

function pickLocale(user: { lang?: string } | null): Locale {
  return user?.lang === "ru" ? "ru" : "en"
}

export function IntegrationCard({ item, connection, onClick }: Props) {
  const t = useBookingT()
  const { user } = useUser()
  const locale = pickLocale(user)
  const status = deriveStatus(item, connection)
  const description = item.short_description?.[locale]

  return (
    <button
      type="button"
      onClick={() => onClick(item.id)}
      className={cn(
        "group relative flex w-full flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-4 text-left",
        "transition-colors hover:border-brand-300 hover:text-brand-500 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
      )}
      aria-label={item.name}
    >
      <div className="flex items-start justify-between gap-3">
        <IntegrationLogo src={item.logo} alt={item.name} size="md" />
        <ChevronRight className="size-5 shrink-0 text-neutral-500 transition-transform group-hover:translate-x-0.5" />
      </div>

      <div className="space-y-1">
        <h3 className="text-base font-semibold text-neutral-900">{item.name}</h3>
        {description && (
          <p className="text-sm text-neutral-600 line-clamp-2">{description}</p>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-2">
        <span className="text-xs text-neutral-700">{item.category}</span>
        <StatusBadge status={status} />
      </div>

      {item.popular && status === "available" && (
        <span className="absolute right-3 top-3 rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
          {t("integrations_popular", "Popular")}
        </span>
      )}
    </button>
  )
}
